import React, { useState } from 'react';
import {
    JeopardyBoardConfig,
    JeopardySessionSettings,
    JeopardyTeam,
    JEOPARDY_TEAM_AVATARS,
    JEOPARDY_TEAM_COLORS,
} from './jeopardy-types';
import { getJeopardyBoardConfigValidationErrors } from './jeopardy-config-validation';

interface Props {
    boardConfig: JeopardyBoardConfig | null;
    onStartGame: (settings: JeopardySessionSettings) => void;
}

const MAX_TEAMS = 8;

export const JeopardyLobby = ({ boardConfig, onStartGame }: Props) => {
    const [teams, setTeams] = useState<JeopardyTeam[]>([
        { id: 't1', name: 'The Brainiacs', score: 0, color: JEOPARDY_TEAM_COLORS[3].hex, avatar: '🧠' },
        { id: 't2', name: 'Trivia Titans', score: 0, color: JEOPARDY_TEAM_COLORS[1].hex, avatar: '⚡' },
    ]);
    const [newTeamName, setNewTeamName] = useState('');
    const [selectedAvatar, setSelectedAvatar] = useState(JEOPARDY_TEAM_AVATARS[0]);
    const [selectedColor, setSelectedColor] = useState(JEOPARDY_TEAM_COLORS[0].hex);
    const [timerSeconds, setTimerSeconds] = useState(15);
    const boardValidationErrors = getJeopardyBoardConfigValidationErrors(boardConfig);
    const hasValidBoardConfig = boardValidationErrors.length === 0;

    const addTeam = (e: React.FormEvent) => {
        e.preventDefault();
        const name = newTeamName.trim();
        if (!name) return;
        if (teams.length >= MAX_TEAMS) return;
        if (teams.some(t => t.name.toLowerCase() === name.toLowerCase())) {
            alert('A team with that name already exists.');
            return;
        }
        setTeams(prev => [
            ...prev,
            { id: `team-${Date.now()}`, name, score: 0, color: selectedColor, avatar: selectedAvatar },
        ]);
        setNewTeamName('');
        const nextAv = Math.floor(Math.random() * JEOPARDY_TEAM_AVATARS.length);
        setSelectedAvatar(JEOPARDY_TEAM_AVATARS[nextAv]);
        const nextCol = Math.floor(Math.random() * JEOPARDY_TEAM_COLORS.length);
        setSelectedColor(JEOPARDY_TEAM_COLORS[nextCol].hex);
    };

    const removeTeam = (id: string) => setTeams(prev => prev.filter(t => t.id !== id));

    const handleLaunch = () => {
        if (!boardConfig) {
            alert('This quiz has no Jeopardy board configured yet. Please configure the board in the quiz settings first.');
            return;
        }
        if (!hasValidBoardConfig) {
            alert(
                `Cannot start Jeopardy match until the board is valid:\n\n${boardValidationErrors
                    .slice(0, 5)
                    .map((error, idx) => `${idx + 1}. ${error}`)
                    .join('\n')}`
            );
            return;
        }
        if (teams.length === 0) {
            alert('Please add at least one team to start.');
            return;
        }
        onStartGame({ teams, timerSeconds });
    };

    return (
        <div className="jeopardy-lobby">
            <div className="jeopardy-lobby__header">
                {boardConfig ? (
                    <>
                        <div className="jeopardy-lobby__board-info">
                            <span className="jeopardy-lobby__board-badge">
                                Board ready: {boardConfig.columnsCount} categories ×{' '}
                                {boardConfig.rowsCount} clues
                                {boardConfig.hasDoubleJeopardy ? ' · Double Jeopardy enabled' : ''}
                            </span>
                        </div>
                        {!hasValidBoardConfig && (
                            <div className="jeopardy-lobby__no-board">
                                ⚠️ Board configuration is incomplete. Fix board warnings in quiz settings before starting.
                            </div>
                        )}
                    </>
                ) : (
                    <div className="jeopardy-lobby__no-board">
                        ⚠️ No board configured. Go to quiz settings → Board tab to create the Jeopardy board.
                    </div>
                )}
            </div>

            <div className="jeopardy-lobby__content">
                {/* Teams section */}
                <div className="jeopardy-lobby__teams-section">
                    <h3>Competing Teams ({teams.length}/{MAX_TEAMS})</h3>

                    {teams.length === 0 ? (
                        <p className="jeopardy-lobby__no-teams">No teams yet. Add teams below.</p>
                    ) : (
                        <div className="jeopardy-lobby__teams-list">
                            {teams.map(team => (
                                <div
                                    key={team.id}
                                    className="jeopardy-lobby__team-card"
                                    style={{ borderLeft: `4px solid ${team.color}` }}
                                >
                                    <span className="jeopardy-lobby__team-avatar">{team.avatar}</span>
                                    <span className="jeopardy-lobby__team-name">{team.name}</span>
                                    <button
                                        type="button"
                                        className="jeopardy-lobby__remove-team"
                                        onClick={() => removeTeam(team.id)}
                                        aria-label={`Remove team ${team.name}`}
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Add team form */}
                    {teams.length < MAX_TEAMS && (
                        <form onSubmit={addTeam} className="jeopardy-lobby__add-team-form">
                            <h4>Add Team</h4>
                            <div className="jeopardy-lobby__add-team-row">
                                <input
                                    type="text"
                                    placeholder="Team name"
                                    value={newTeamName}
                                    onChange={e => setNewTeamName(e.target.value)}
                                />
                                <select
                                    value={selectedAvatar}
                                    onChange={e => setSelectedAvatar(e.target.value)}
                                    aria-label="Team avatar"
                                >
                                    {JEOPARDY_TEAM_AVATARS.map(av => (
                                        <option key={av} value={av}>{av}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="jeopardy-lobby__color-row">
                                <label>Team color:</label>
                                <div className="jeopardy-lobby__color-swatches">
                                    {JEOPARDY_TEAM_COLORS.map(c => (
                                        <button
                                            key={c.hex}
                                            type="button"
                                            className={`jeopardy-lobby__color-swatch${selectedColor === c.hex ? ' jeopardy-lobby__color-swatch--active' : ''}`}
                                            style={{ backgroundColor: c.hex }}
                                            title={c.name}
                                            onClick={() => setSelectedColor(c.hex)}
                                            aria-label={c.name}
                                        />
                                    ))}
                                </div>
                            </div>
                            <button type="submit" className="jeopardy-lobby__add-btn">
                                + Add Team
                            </button>
                        </form>
                    )}
                </div>

                {/* Timer settings */}
                <div className="jeopardy-lobby__timer-section">
                    <h3>Answer Timer</h3>
                    <div className="jeopardy-lobby__timer-row">
                        <label htmlFor="timer-seconds">Seconds per clue:</label>
                        <input
                            id="timer-seconds"
                            type="number"
                            min={5}
                            max={60}
                            value={timerSeconds}
                            onChange={e => setTimerSeconds(Math.max(5, Math.min(60, parseInt(e.target.value) || 15)))}
                        />
                    </div>
                </div>
            </div>

            <div className="jeopardy-lobby__launch">
                <button
                    className="jeopardy-lobby__launch-btn"
                    onClick={handleLaunch}
                    disabled={!boardConfig || !hasValidBoardConfig || teams.length === 0}
                >
                    🏆 Start Jeopardy Match
                </button>
            </div>
        </div>
    );
};
